import { useEffect, useState, type FunctionComponent } from "react";
import { Outlet } from "react-router-dom";
import Spinner from "../../../../components/spinner.components";
import type { IPagination } from "../../../../types/models/pagination.models.types";
import {
	type IPost,
	PostStatus,
} from "../../../../types/models/post.models.types";
import PaginationComponent from "../../../../components/pagination.components";
import { CampaignPostBrandPartial } from "./campaign-post.brand.partials";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../../../../components/ui/tabs";
import { useToast } from "../../../../hooks/use-toast";
import usePostStore from "../../../../store/post.store";
import { handleHttpError } from "../../../../utils/utils";
import { useGetPostsEmphemeralHttp } from "../../../../hooks/http/ephemeral/campaigns/get-posts.emphemeral.http";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../../components/ui/select";

interface IProps {
	initialStatus: PostStatus;
	tabs: Array<{
		status: PostStatus;
		title: string;
		flagged: boolean;
	}>;

	campaignId: string;
}

enum PostSortBy {
	CREATED_AT_NEWEST = "createdAtNewest",
	CREATED_AT_OLDEST = "createdAtOldest",
	LIKES = "likes",
	VIEWS = "views",
	COMMENTS = "comments",
	SHARES = "shares",
}

const CampaignPostsBrandPartial: FunctionComponent<IProps> = ({
	tabs,
	initialStatus,
	campaignId,
}) => {
	const [status, setStatus] = useState<PostStatus>(initialStatus);
	const {
		fetching,
		execute: getPostsHttp,
		cancel: cancelGetPostsHttp,
	} = useGetPostsEmphemeralHttp();
	const [pageNo, setPageNo] = useState(1);
	const [paginationData, setPaginationData] = useState<IPagination>();
	const { toast } = useToast();
	const { setData, data: postStore } = usePostStore();
	const [sortBy, setSortBy] = useState<PostSortBy>(PostSortBy.CREATED_AT_NEWEST);
	const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

	async function getPosts(page: number, currentStatus: PostStatus, currentSortBy: PostSortBy, currentSortOrder: "asc" | "desc") {
		cancelGetPostsHttp();

		let apiSortBy = "createdAt";
		let apiSortOrder: "asc" | "desc" = currentSortOrder;

		if (currentSortBy === PostSortBy.CREATED_AT_NEWEST) {
			apiSortBy = "createdAt";
			apiSortOrder = "desc";
		} else if (currentSortBy === PostSortBy.CREATED_AT_OLDEST) {
			apiSortBy = "createdAt";
			apiSortOrder = "asc";
		} else if (
			currentSortBy === PostSortBy.LIKES ||
			currentSortBy === PostSortBy.VIEWS ||
			currentSortBy === PostSortBy.COMMENTS ||
			currentSortBy === PostSortBy.SHARES
		) {
			// metrics always sorted desc from backend
			apiSortBy = currentSortBy;
			apiSortOrder = "desc";
		}

		const response = await getPostsHttp({
			body: null,
			queryParams: {
				status: currentStatus,
				campaignId,
				page,
				sortBy: apiSortBy,
				sortOrder: apiSortOrder,
			},
		});

		if (!response.success) {
			return handleHttpError(response, toast);
		}

		setData((prevData) => {
			const newPosts = response.data.posts.filter((post) => {
				const isVisibleLivePost =
					currentStatus !== PostStatus.postApproved ||
					post.postStatus.status === PostStatus.postApproved;
				return (
					isVisibleLivePost &&
					prevData.findIndex((p) => p.id === post.id) === -1
				);
			});

			return [...prevData, ...newPosts];
		});

		setPaginationData(response.data.pagination);
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		setData([]);
		getPosts(1, status, sortBy, sortOrder);
	}, [campaignId]);



	return (
		<div className="flex flex-col gap-8 pt-8 w-full min-w-0">
			<Tabs
				className="w-full"
				value={status}
				onValueChange={(value) => {
					const nextpage = 1;
					const nextSatus = value as PostStatus;

					setData([]);
					setStatus(nextSatus);
					setPageNo(1);
					getPosts(nextpage, nextSatus, sortBy, sortOrder);
				}}
			>
				<div className="flex md:flex-row flex-col justify-between md:items-center gap-4 mb-4 w-full min-w-0">
					<div className="w-full md:w-auto min-w-0 overflow-x-auto no-scrollbar">
						<TabsList className="flex-nowrap justify-start w-max min-w-full md:min-w-0">
						{tabs.map((tab) => {
							return (
								<TabsTrigger
									key={tab.status}
									className={`border-0 text-xs md:text-base shrink-0 whitespace-nowrap border-b-2 md:px-4 px-3 py-2 ${
										tab.status === status ? "" : "border-border"
									}`}
									value={tab.status}
								>
									{tab.title}
								</TabsTrigger>
							);
						})}
						</TabsList>
					</div>
					<div className="flex items-center gap-2 shrink-0">
						<Select
							value={sortBy}
							onValueChange={(value) => {
								const nextSortBy = value as PostSortBy;
								setData([]);
								setPageNo(1);
								setSortBy(nextSortBy);

								if (nextSortBy === PostSortBy.CREATED_AT_NEWEST) {
									setSortOrder("desc");
									getPosts(1, status, nextSortBy, "desc");
								} else if (nextSortBy === PostSortBy.CREATED_AT_OLDEST) {
									setSortOrder("asc");
									getPosts(1, status, nextSortBy, "asc");
								} else {
									// metrics: always desc
									setSortOrder("desc");
									getPosts(1, status, nextSortBy, "desc");
								}
							}}
						>
							<SelectTrigger className="gap-3 bg-tertiarybuttonbg border-border rounded-lg w-fit">
								<span className="text-secondary">Sort by:</span>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value={PostSortBy.CREATED_AT_NEWEST}>
									Newest first
								</SelectItem>
								<div className="border-border border-t" />
								<SelectItem value={PostSortBy.CREATED_AT_OLDEST}>
									Oldest first
								</SelectItem>
								{status === PostStatus.postApproved && (
									<>
										<div className="border-border border-t" />

										<SelectItem value={PostSortBy.LIKES}>Likes</SelectItem>
										<div className="border-border border-t" />

										<SelectItem value={PostSortBy.VIEWS}>Views</SelectItem>
										<div className="border-border border-t" />

										<SelectItem value={PostSortBy.COMMENTS}>
											Comments
										</SelectItem>
										<div className="border-border border-t" />

										<SelectItem value={PostSortBy.SHARES}>Shares</SelectItem>
									</>
								)}
							</SelectContent>
						</Select>
					</div>
				</div>
				{tabs.map((tab) => {
					return (
						<TabsContent key={tab.status} value={tab.status} className="py-12">
							{fetching && postStore.length < 1 ? (
								<div className="flex justify-center items-center w-full h-96">
									<Spinner />
								</div>
							) : (
								<>
									<div className="gap-6 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 w-full">
										{postStore.map((post: IPost) => (
											<CampaignPostBrandPartial key={post.id} post={post} />
										))}
									</div>

									{paginationData && (
										<PaginationComponent
											fetching={fetching}
											paginationData={paginationData}
											onClick={() => {
												const nextPage = pageNo + 1;
												setPageNo(nextPage);
												getPosts(nextPage, status, sortBy, sortOrder);
											}}
										/>
									)}
									{postStore.length === 0 && (
										<div className="flex justify-center items-center w-full h-96">
											<p>No posts found </p>
										</div>
									)}
								</>
							)}
						</TabsContent>
					);
				})}
			</Tabs>
			<Outlet />
		</div>
	);
};

export default CampaignPostsBrandPartial;
